import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { Button } from "rhombus-react";

import { closeModal } from "reducers/Modal";
import { TextOverflow } from "utils/common";
import BasicModal from "widgets/modals/BasicModal";

import "./AfterEffectsUploadErrors.css";

function mergeErrorsByFilename(errors){
	/*
		`errors` is an array in the form:
		```
			[
				{
					filename: "100%WholeGrain.mp4",
					error: "The file name 100%WholeGrainPasta.mp4 is invalid.  Illegal characters."
				},
				{
					filename: "100%WholeGrain.mp4",
					error: "The file name 100%WholeGrainPasta.mp4 is gross.  Bran doesn't belong in pasta."
				},
				...
			]
		```

		There may be multiple error objects for a given filename, so we want to combine them into
		an array per filename.

		We return an array in almost the same format, but with an `errors` array.
		```
			[
				{
					filename: "100%WholeGrain.mp4",
					errors: [
						"The file name 100%WholeGrainPasta.mp4 is invalid.  Illegal characters.",
						"The file name 100%WholeGrainPasta.mp4 is gross.  Bran doesn't belong in pasta."
				},
				...
			]
		```
	*/
	const merged = errors.reduce((prev, error) => {
		const { filename, error: message } = error || {};
		if(prev[filename]){
			prev[filename].push(message);
		} else {
			prev[filename] = [message];
		}
		return prev;
	}, {});

	return Object.entries(merged).map(([filename, list]) => ({ filename, errors: list }));
}

export default function AfterEffectsUploadErrors(props){
	const {
		filename,
		errors,
	} = props;

	const dispatch = useDispatch();

	const baseName = filename?.replace(/\.zip$/, "");
	const mergedErrors = useMemo(() => mergeErrorsByFilename(errors), [errors]);

	function onClose(){
		dispatch(closeModal());
	}

	function renderContent(){
		return (
			<div className="scrolling-modal-body">
				<p>
					The upload was canceled due to the following errors.  Please make the necessary adjustments and try again.
				</p>

				<table className="basic-table">
					<thead>
						<tr>
							<th className="name-col">Asset Name</th>
							<th>Error Message</th>
						</tr>
					</thead>
					<tbody>
						{mergedErrors.map((message) => {
							return (
								<tr key={message.filename}>
									<td className="name-col">
										<TextOverflow value={message.filename} />
									</td>
									<td>
										{message.errors.map(error => <div className="message" key={error}>{error}</div>)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}

	function renderFooter(){
		return (
			<Button theme="default" onClick={onClose}>Close</Button>
		);
	}

	const modalProps = {
		title: `After Effects Package Upload Error - ${baseName}`,
		className: "ae-upload-errors-modal",
		onClose,
		renderContent,
		renderFooter,
	};

	return <BasicModal {...modalProps} />;
}

AfterEffectsUploadErrors.displayName = "AfterEffectsUploadErrors";
AfterEffectsUploadErrors.defaultProps = {
	errors: [],
};
